<template>
  <!-- ── Horizontal palette strip ──────────────────────────────────────── -->
  <div class="palette-bar">
    <div class="palette-section">
      <!-- Equipment dropdown -->
      <v-select
        v-model="selectedEquipment"
        :items="availableEquipment"
        item-title="equipmentName"
        item-value="equipmentId"
        return-object
        density="compact"
        variant="outlined"
        placeholder="Pick equipment…"
        hide-details
        clearable
        class="catalog-select"
      >
        <!-- Icon inside the selection box -->
        <template #prepend-inner>
          <img
            v-if="selectedEquipment && getEquipmentSvg(selectedEquipment.equipmentTypeName)"
            :src="getEquipmentSvg(selectedEquipment.equipmentTypeName)!"
            :alt="selectedEquipment.equipmentTypeName"
            class="select-icon"
            draggable="false"
          />
          <v-icon v-else-if="selectedEquipment" size="22" color="primary">mdi-chip</v-icon>
        </template>

        <!-- Icon + name in each list item -->
        <template #item="{ internalItem, props: itemProps }">
          <v-list-item v-bind="itemProps">
            <template #prepend>
              <img
                v-if="getEquipmentSvg(internalItem.raw.equipmentTypeName)"
                :src="getEquipmentSvg(internalItem.raw.equipmentTypeName)!"
                :alt="internalItem.raw.equipmentTypeName"
                class="select-icon mr-2"
                draggable="false"
              />
              <v-icon v-else size="22" color="primary" class="mr-2">mdi-chip</v-icon>
            </template>
          </v-list-item>
        </template>
      </v-select>

      <!-- Drag chip (shown once equipment is selected) -->
      <v-chip
        v-if="selectedEquipment"
        :draggable="true"
        color="blue"
        size="large"
        variant="elevated"
        elevation="4"
        prepend-icon="mdi-drag"
        class="drag-chip"
        @dragstart="onDragStart"
      >
        {{ selectedEquipment.equipmentName }}
      </v-chip>
      <span v-else class="drag-hint text-no-wrap">
        Select equipment, then drag onto the map to place
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { IEquipment, IEquipmentSystemGroup } from '../types';
import { getEquipmentSvg } from '../types';

const props = defineProps<{
  equipmentGroups: IEquipmentSystemGroup[];
  /** IDs of equipment already placed on the canvas across all twins. */
  placedEquipmentIds: number[];
  /** Site system id of the active digital twin — restricts the equipment list. */
  currentSiteSystemId?: number | null;
}>();

const selectedEquipment = ref<IEquipment | null>(null);

const availableEquipment = computed<IEquipment[]>(() => {
  const group = props.equipmentGroups.find(
    (g) => g.siteSystemId === props.currentSiteSystemId
  );
  return (group?.equipments ?? []).filter(
    (e) => !props.placedEquipmentIds.includes(e.equipmentId)
  );
});

// Clear equipment selection if the placed item is no longer available
watch(availableEquipment, (list) => {
  if (
    selectedEquipment.value &&
    !list.some((e) => e.equipmentId === selectedEquipment.value!.equipmentId)
  ) {
    selectedEquipment.value = null;
  }
});

// Reset selection when the groups list reloads or the active twin changes
watch(
  [() => props.equipmentGroups, () => props.currentSiteSystemId],
  () => {
    selectedEquipment.value = null;
  }
);

const onDragStart = (e: DragEvent): void => {
  if (!e.dataTransfer || !selectedEquipment.value) return;
  const group = props.equipmentGroups.find(
    (g) => g.equipments.some((eq) => eq.equipmentId === selectedEquipment.value!.equipmentId)
  );
  e.dataTransfer.effectAllowed = 'copy';
  e.dataTransfer.setData(
    'application/json',
    JSON.stringify({
      equipment: selectedEquipment.value,
      siteSystemId: group?.siteSystemId,
      siteSystemName: group?.siteSystemName,
    })
  );
};
</script>

<style lang="scss" scoped>
.palette-bar {
  display: flex;
  align-items: center;
  min-height: 52px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background: rgba(0, 0, 0, 0.02);
  overflow-x: auto;
  flex-shrink: 0;
}

.palette-section {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 20px;
  min-width: 0;
}

.catalog-select {
  min-width: 220px;
  max-width: 360px;
  flex: 1;
}

.select-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
}

.drag-chip {
  cursor: grab;
  user-select: none;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
}

.drag-hint {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.38);
  font-style: italic;
}
</style>
